"use client";

import * as React from "react";
import type { CSSProperties, ReactNode } from "react";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { ScrollEdgeArrows, useScrollEdges } from "./scroll-edges";

/**
 * The subcategory row under a listing page's title.
 *
 * Two things the plain `overflow-x-auto no-scrollbar` row could not do:
 *
 *  - It hid its scrollbar, so a row that ran past the edge of the screen gave
 *    no sign that it did. It now carries the same ‹ / › affordances as the card
 *    carousels — one per direction that can actually move, none when it fits.
 *  - The leading "地產 |" parent link scrolled away with the tabs. QA: "the
 *    major category title 地產 | should be fixed in position." It (and the
 *    compact variant's bold label) now sit OUTSIDE the scroll port, so the row
 *    always says which section you are in.
 */
export function CategoryHeaderTabRow({
  compact,
  tabsLabel,
  parentLink,
  rowColor,
  children,
}: {
  compact: boolean;
  tabsLabel?: string;
  parentLink?: { label: string; href: string };
  rowColor?: boolean;
  children: ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const edges = useScrollEdges(ref);

  const rowStyle: CSSProperties = rowColor
    ? { fontFamily: CORE_FONT.sans, color: CORE_COLORS.textPrimary }
    : { fontFamily: CORE_FONT.sans };
  const gap = compact ? "gap-[16px] text-[14px]" : "gap-[20px]";
  const hasLead = Boolean((compact && tabsLabel) || parentLink);

  return (
    <div
      data-tab-row=""
      className={`flex items-center ${gap} min-w-0`}
      style={rowStyle}
    >
      {/* Pinned lead — never scrolls. */}
      {compact && tabsLabel && (
        <span className="font-bold shrink-0">{tabsLabel}</span>
      )}
      {parentLink && (
        <>
          <a
            href={parentLink.href}
            className="shrink-0 font-bold transition-colors hover:opacity-70"
            style={{
              color: CORE_COLORS.textPrimary,
              fontSize: 15,
              lineHeight: "20px",
            }}
          >
            {parentLink.label}
          </a>
          <span
            aria-hidden
            className="shrink-0 select-none"
            style={{
              color: CORE_COLORS.textTertiary,
              fontSize: 15,
              lineHeight: "20px",
            }}
          >
            |
          </span>
        </>
      )}

      <div className={`relative min-w-0 flex-1${hasLead ? "" : " w-full"}`}>
        <div
          ref={ref}
          className={`flex items-center ${gap} overflow-x-auto no-scrollbar whitespace-nowrap`}
        >
          {children}
        </div>
        <ScrollEdgeArrows scroller={ref} edges={edges} />
      </div>
    </div>
  );
}
